<template>
  <b-modal
    :visible="visible"
    :title="isTypeMode ? $t('editDispatchTypeReason.titleType') : $t('editDispatchTypeReason.titleReason')"
    size="md"
    centered
    lazy
    :ok-title="$t('common.save')"
    :cancel-title="$t('common.cancel')"
    :ok-disabled="(isTypeMode ? !form.dispatch_type_id : !form.dispatch_reason_id) || saving"
    @shown="onShown"
    @hide="$emit('update:visible', false)"
    @ok="save"
  >
    <div v-if="loading" class="text-center py-3">
      <b-spinner variant="primary" />
    </div>

    <div v-else>
      <div v-if="isTypeMode" class="form-group">
        <label>{{ $t('editDispatchTypeReason.typeLabel') }}</label>
        <v-select
          v-model="form.dispatch_type_id"
          :options="dispatchTypes"
          :reduce="d => d.id"
          label="name"
          :placeholder="$t('editDispatchTypeReason.typePlaceholder')"
          class="vselect"
        />
        <small class="text-muted d-block mt-1">{{ $t('editDispatchTypeReason.typeChangeHint') }}</small>
      </div>

      <div v-else class="form-group">
        <label>{{ $t('editDispatchTypeReason.reasonLabel') }}</label>
        <v-select
          v-model="form.dispatch_reason_id"
          :options="dispatchReasons"
          :reduce="d => d.id"
          label="name"
          :placeholder="$t('editDispatchTypeReason.reasonPlaceholder')"
          class="vselect"
        />
      </div>

      <small v-if="error" class="text-danger d-block mt-1">{{ error }}</small>
    </div>
  </b-modal>
</template>

<script>
import { BSpinner } from 'bootstrap-vue'
import vSelect from 'vue-select'
import ApiErrorMixin from '@/mixins/ApiErrorMixin'

export default {
  name: 'EditDispatchTypeReasonModal',

  components: { BSpinner, vSelect },

  mixins: [ApiErrorMixin],

  props: {
    visible: { type: Boolean, default: false },
    mode: { type: String, default: 'type', validator: v => ['type', 'reason'].includes(v) },
    occurrenceId: { type: [Number, String], default: null },
    nodeId: { type: [Number, String], default: null },
    disciplineId: { type: [Number, String], default: null },
    currentDispatchTypeId: { type: [Number, String], default: null },
    currentDispatchReasonId: { type: [Number, String], default: null },
  },

  data() {
    return {
      dispatchTypes: [],
      dispatchReasons: [],
      loading: false,
      saving: false,
      error: null,
      form: {
        dispatch_type_id: null,
        dispatch_reason_id: null,
      },
    }
  },

  computed: {
    isTypeMode() {
      return this.mode === 'type'
    },
  },

  methods: {
    async onShown() {
      this.error = null
      this.form.dispatch_type_id = this.currentDispatchTypeId || null
      this.form.dispatch_reason_id = this.currentDispatchReasonId || null

      this.loading = true
      try {
        if (this.isTypeMode) {
          await this.loadTypes()
        } else {
          await this.loadReasons(this.currentDispatchTypeId)
        }
      } finally {
        this.loading = false
      }
    },

    async loadTypes() {
      try {
        const res = await this.$http.get(`${this.$store.state.api}dispatch_types/for-node`, {
          params: { discipline_id: this.disciplineId, node_id: this.nodeId, status: 1 },
        })
        this.dispatchTypes = res.data.data || []
      } catch (e) {
        this.dispatchTypes = []
      }
    },

    async loadReasons(typeId) {
      if (!typeId) {
        this.dispatchReasons = []
        return
      }
      try {
        const res = await this.$http.get(`${this.$store.state.api}dispatch_reasons/for-node`, {
          params: { dispatch_type_id: typeId, node_id: this.nodeId, status: 1 },
        })
        this.dispatchReasons = res.data.data || []
      } catch (e) {
        this.dispatchReasons = []
      }
    },

    async save(bvModalEvent) {
      bvModalEvent.preventDefault()

      this.saving = true
      this.error = null
      try {
        // No modo "tipo", o motivo atual deixa de valer pro tipo novo — some
        // até o operador escolher um novo pelo lápis do Motivo. No modo
        // "motivo", o tipo não muda, só o motivo dentro dele.
        const payload = this.isTypeMode
          ? { dispatch_type_id: this.form.dispatch_type_id, dispatch_reason_id: null }
          : { dispatch_type_id: this.currentDispatchTypeId, dispatch_reason_id: this.form.dispatch_reason_id }

        const res = await this.$http.post(
          `${this.$store.state.api}occurrences/${this.occurrenceId}/dispatch-type-reason`,
          payload,
        )
        this.$emit('updated', res.data.data)
        this.$emit('update:visible', false)
      } catch (e) {
        this.error = (e.response && typeof e.response.data === 'string')
          ? e.response.data
          : this.apiErrorText(e, this.$t('editDispatchTypeReason.saveError'))
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
